import { ChangeDetectorRef, Component, Inject, inject, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { PaymentPlanService } from '../../../../services/payment-plan/payment-plan.service';
import { IDatePickerDirectiveConfig } from 'ng2-date-picker';
import moment from 'moment';
import { CustomValidators } from '../../../../utility/custom-validators';
import { RemoveOtherUserPaymentMethodComponent } from '../remove-other-user-payment-method/remove-other-user-payment-method.component';
import { finalize, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { LegalService } from '../../../../services/legal.service';
import {
  AutoPay,
  ClientCurrencies,
  DEFAULT_CURRENCY,
  PaymentMethodType,
  PaymentPlan,
  PaymentPlanApiService,
  PaymentPlanUi,
} from '@aiwyn/api';
import { ConfigService } from '@aiwyn/config';
import { AuthAdaptorService, AuthStateAdaptorService } from '@aiwyn/auth';
import {
  areStringsEqualIgnoreCase,
  compareStringsIgnoreCase,
  daysToMilliseconds,
  DialogSize,
  extractFailureContext,
  getControlValueChanges,
  ignoreFalsy,
  ignoreNullish,
  Logger,
  mapToAndResult,
} from '@aiwyn/shared';
import {
  AbstractPaymentMethod,
  PaymentFlow,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlanTrackService,
  PaymentPlanWorkflowEvent,
  PaymentPlatformService,
  PaymentsWorkflowType,
  RecipientEmailsComponent,
  SelectPaymentMethodComponent,
  StripeState,
} from '@aiwyn/common/payments';
import dayjs from 'dayjs';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { ClearScheduledPayments } from '../../settings/state';

export interface PaymentPlanModalData {
  isEditPaymentPlan: boolean;
  paymentPlanUi: PaymentPlanUi;
  autoPayClients: AutoPay[];
  clientCurrencies: ClientCurrencies;
}

interface ClientOption {
  id: number;
  name: string;
  clientRef: string;
  disabled: boolean;
}

enum PaymentPlanFormKey {
  NAME = 'paymentPlanName',
  CLIENT = 'client',
  AMOUNT = 'amount',
  FREQUENCY = 'frequency',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
}

interface PaymentPlanFormData {
  [PaymentPlanFormKey.NAME]: string;
  [PaymentPlanFormKey.AMOUNT]: number;
  [PaymentPlanFormKey.FREQUENCY]: string;
  [PaymentPlanFormKey.START_DATE]: string;
  [PaymentPlanFormKey.END_DATE]: string;
  [PaymentPlanFormKey.CLIENT]: ClientOption;
}

@Component({
  selector: 'app-payment-plan',
  templateUrl: './payment-plan-modal.component.html',
  styleUrls: ['./payment-plan-modal.component.scss'],
  standalone: false,
  providers: [PaymentPlanTrackService],
})
export class PaymentPlanModalComponent implements OnInit, OnDestroy {
  private readonly _unsubscribe$ = new Subject<void>();
  private readonly paymentPlanTrackService = inject(PaymentPlanTrackService);
  private readonly logger = inject(Logger);

  // Set to true once a terminal PAYMENT_PLAN_* event has fired (CREATED, UPDATED).
  // afterClosed reads this to decide whether to emit PAYMENT_PLAN_CANCELLED.
  private _didEmitTerminalEvent = false;

  // FormGroup
  paymentPlanForm: UntypedFormGroup;

  // Client options
  clientOptions: ClientOption[];

  // Payment method could come from another contact or an admin.
  paymentMethodIsFromUser: boolean = true;

  // Used to disable the start date input field while editing recurring payment if date is before today.
  disableStartDateWhileEditing: boolean = false;

  // Datepicker configs.
  datePickerConfigStartDate: IDatePickerDirectiveConfig = {
    opens: 'right',
    format: 'YYYY-MM-DD',
    min: dayjs(),
  };

  datePickerConfigEndDate: IDatePickerDirectiveConfig = {
    opens: 'left',
    format: 'YYYY-MM-DD',
    min: dayjs(Date.now() + daysToMilliseconds(1)),
  };

  frequencyDisplayMap: Record<string, string> = {
    WEEKLY: 'Weekly',
    BIWEEKLY: 'Bi-Weekly',
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    YEARLY: 'Yearly',
  };

  // will only emit USD for now until we add multicurrency support for payment plans
  selectedClientCurrency$ = defer(() => getControlValueChanges<ClientOption>(this.paymentPlanForm.get(PaymentPlanFormKey.CLIENT))).pipe(
    map(client => this.data.clientCurrencies[client.id]?.data ?? DEFAULT_CURRENCY),
  );

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PaymentPlanModalData,
    public dialog: MatDialog,
    public paymentPlanApiService: PaymentPlanApiService,
    public paymentPlanService: PaymentPlanService,
    public configService: ConfigService,
    public authAdaptor: AuthAdaptorService,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogRef: MatDialogRef<PaymentPlanModalComponent>,
    public legalService: LegalService,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly store: Store,
  ) {
    this.clientOptions = this.paymentPlanService.clientList.map(client => ({
      id: client.id,
      name: client.name,
      clientRef: client['clientRef'] ?? client.ref,
      disabled: data.autoPayClients?.some(({ active, clientId }) => active && clientId === client.id)
        || data.clientCurrencies[client.id]?.data?.code !== 'USD',
    }));
  }

  ngOnInit(): void {
    this.setPaymentMethodIsFromUser$.subscribe();
    this.setSelectedPaymentMethodFromActivePlan$.subscribe();

    this.buildPaymentPlanForm();

    if (this.data.isEditPaymentPlan) {
      this.paymentPlanTrackService.trackWorkflow(
        PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_STARTED,
        { planId: String(this.data.paymentPlanUi?.paymentPlan?.id) },
      );
    } else {
      this.paymentPlanTrackService.trackWorkflow(PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_STARTED);
    }

    // Catches every dismiss path (Cancel button, X, ESC, backdrop) in one place.
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      if (!this._didEmitTerminalEvent) {
        this.paymentPlanTrackService.trackWorkflow(PaymentPlanWorkflowEvent.PAYMENT_PLAN_CANCELLED);
      }
    });
  }

  getAddPlanMethodForm(): UntypedFormGroup {
    return this.formBuilder.group(
      {
        [PaymentPlanFormKey.NAME]: ['', Validators.required],
        [PaymentPlanFormKey.CLIENT]: ['', Validators.required],
        [PaymentPlanFormKey.AMOUNT]: ['', [Validators.required, Validators.min(0)]],
        [PaymentPlanFormKey.FREQUENCY]: ['MONTHLY', Validators.required],
        [PaymentPlanFormKey.START_DATE]: ['', Validators.required],
        [PaymentPlanFormKey.END_DATE]: ['', Validators.required],
      },
      { validators: CustomValidators.endDateGreaterThanStartDate() },
    );
  }

  getEditPlanMethodForm(paymentPlan: any, clientId: number): UntypedFormGroup {
    return this.formBuilder.group(
      {
        [PaymentPlanFormKey.NAME]: [paymentPlan.name, Validators.required],
        [PaymentPlanFormKey.CLIENT]: [
          this.clientOptions.find(({ id }) => id === clientId),
          Validators.required,
        ],
        [PaymentPlanFormKey.AMOUNT]: [paymentPlan.amount, [Validators.required, Validators.min(0)]],
        [PaymentPlanFormKey.FREQUENCY]: [paymentPlan.frequency, Validators.required],
        [PaymentPlanFormKey.START_DATE]: [new Date(paymentPlan.startDate).toISOString().slice(0, 10), Validators.required],
        [PaymentPlanFormKey.END_DATE]: [new Date(paymentPlan.endDate).toISOString().slice(0, 10), Validators.required],
      },
      { validators: CustomValidators.endDateGreaterThanStartDate() },
    );
  }

  /**
   * Builds recurring payment form and determines if the modal should display saved recurring payment data
   * if the user is editing.  Also Implements form validation.
   */
  buildPaymentPlanForm(): void {
    // build form group
    this.paymentPlanForm = this.data.isEditPaymentPlan
      ? this.getEditPlanMethodForm(this.data.paymentPlanUi.paymentPlan, this.data.paymentPlanUi.client.id)
      : this.getAddPlanMethodForm();

    // start date validators/configs
    if (this.configService.paymentPlanV4Enabled) {
      // V4 payment plans ALWAYS need the start validator
      this.paymentPlanForm.get('startDate').setValidators([Validators.required, CustomValidators.startDateAfterToday]);

      const tomorrow: Date = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      this.datePickerConfigStartDate = { ...this.datePickerConfigStartDate, min: dayjs(tomorrow) };
      this.datePickerConfigEndDate = { ...this.datePickerConfigEndDate, min: dayjs(tomorrow.getTime() + daysToMilliseconds(1)) };
    } else if (!this.data.isEditPaymentPlan) {
      // V3 Payment plans should only have the validator on edit
      // If the user is creating a new recurring payment, make sure the start date is not before today.
      // This can be allowed when the user is editing the payment plan.
      this.paymentPlanForm.get('startDate').setValidators([Validators.required, CustomValidators.startDateMinimum]);
    }

    // Sets disableStartDateWhileEditing.
    this.data.isEditPaymentPlan ? this.disableStartDateWhileEditing = this.isStartDateBeforeCurrentDate() : false;
  }

  /**
   * Determines if start date of this.data.paymentPlanUi is before today. If it is a flag is set to disable the start date input field.
   */
  isStartDateBeforeCurrentDate(): boolean {
    return moment(this.data.paymentPlanUi.paymentPlan.startDate, 'YYYY-MM-DD') < moment(moment().format('YYYY-MM-DD'), 'YYYY-MM-DD');
  }

  getPaymentPlanPayload(paymentPlatFormData: PaymentPlanFormData, paymentPlanCreatedByIds: PaymentPlanCreatedByIds): PaymentPlan {
    let id: string, type: string, lastFour: string, country: string, brand: string, cardZip: string;
    if (this.paymentMethodIsFromUser) {
      id = this.selectedPaymentMethod$.value.id;
      type = (this.selectedPaymentMethod$.value.subType ?? this.selectedPaymentMethod$.value.type).toUpperCase();
      lastFour = this.selectedPaymentMethod$.value.lastFour;
      country = this.selectedPaymentMethod$.value.country;
      brand = this.selectedPaymentMethod$.value.brand;

      if (this.selectedPaymentMethod$.value.type === PaymentMethodType.CARD) {
        cardZip = this.selectedPaymentMethod$.value.billingDetails.address?.postalCode;
      }
    } else {
      id = this.data.paymentPlanUi.paymentPlan.paymentMethodId;
      type = this.data.paymentPlanUi.paymentPlan.paymentMethod as PaymentMethodType;
      lastFour = this.data.paymentPlanUi.paymentPlan.last4;
      country = this.data.paymentPlanUi.paymentPlan.country;
      brand = this.data.paymentPlanUi.paymentPlan.cardType;
      cardZip = this.data.paymentPlanUi.paymentPlan.zip;
    }

    return {
      id: this.data.paymentPlanUi ? this.data.paymentPlanUi.paymentPlan.id : null,
      name: paymentPlatFormData.paymentPlanName,
      amount: paymentPlatFormData.amount,
      startDate: paymentPlatFormData.startDate,
      endDate: paymentPlatFormData.endDate,
      frequency: paymentPlatFormData.frequency,
      createdByContactId: paymentPlanCreatedByIds.contactId,
      createdByStaffId: paymentPlanCreatedByIds.staffId,
      clientId: +paymentPlatFormData.client.id,
      paymentMethodId: id,
      paymentMethod: type,
      last4: lastFour,
      cardType: brand,
      zip: cardZip,
      country: country,
      active: true,
      emailConfirmationAddressList: [this.primaryEmail].concat(this.additionalEmails).filter(Boolean),
      termsAccepted: true,
      usState: this.data.paymentPlanUi?.paymentPlan.usState,
      createdAt: null,
      updatedAt: null,
      lastUpdatedByStaffId: paymentPlanCreatedByIds.staffId,
      lastUpdatedByContactId: paymentPlanCreatedByIds.contactId,
    };
  }

  readonly PaymentMethodIntent = PaymentMethodIntent;
  readonly PaymentFlow = PaymentFlow;
  readonly PaymentPlanFormKey = PaymentPlanFormKey;
  readonly DialogSize = DialogSize;
  readonly PaymentsWorkflowType = PaymentsWorkflowType;

  compareClientsFn = (clientA: ClientOption, clientB: ClientOption): boolean => clientA && clientB && clientA.id === clientB.id;
  clientsTriggerDisplayFn = (client: ClientOption): string => {
    const disabledReason = client.disabled
      ? `&nbsp;– ${this.data.clientCurrencies[client.id]?.data?.code !== 'USD' ? 'Non-USD' : 'AutoPay Enabled'}`
      : '';

    return `${client.name} (${client.clientRef})${disabledReason}`;
  };
  clientsSortFn = (clientA: ClientOption, clientB: ClientOption): number => compareStringsIgnoreCase(clientA.name, clientB.name);
  clientsDisableOptionFn = (client: ClientOption): boolean => client.disabled;
  panelWidth$ = new BehaviorSubject<number>(undefined);

  recurringPaymentAmount$ = defer(() => getControlValueChanges<string>(this.paymentPlanForm.get(PaymentPlanFormKey.AMOUNT)).pipe(
    map(Number),
  ));

  primaryEmail = this.authStateAdaptor.getUserEmailSnapshot();
  additionalEmails: string[] = (this.data.paymentPlanUi?.paymentPlan.emailConfirmationAddressList ?? [])
    .filter(email => !areStringsEqualIgnoreCase(email, this.primaryEmail));
  onAdditionalEmailsChanged(emails: string[]): void {
    this.additionalEmails = emails;
  }

  defaultPaymentMethod$ = new BehaviorSubject<AbstractPaymentMethod>(undefined);
  selectedPaymentMethod$ = new BehaviorSubject<AbstractPaymentMethod>(undefined);
  selectedPaymentMethodId$ = this.selectedPaymentMethod$.pipe(
    map(selectedPaymentMethod => selectedPaymentMethod?.id),
    startWith(this.data.paymentPlanUi?.paymentPlan.paymentMethodId),
  );
  // This observable does not when returning undefined (if payment method is from another user or plan is not active).
  setSelectedPaymentMethodFromActivePlan$ = this.paymentPlatformService.getSavedPaymentMethod(
    this.data.paymentPlanUi?.paymentPlan.paymentMethodId,
  ).pipe(
    take(1),
    ignoreNullish(),
    tap(selectedPaymentMethod => this.selectedPaymentMethod$.next(selectedPaymentMethod)),
  );

  onSavedPaymentMethodSelected(selectedPaymentMethod: AbstractPaymentMethod): void {
    // ignore this event if the plan's payment method is from another user or has not been retrieved yet
    this.defaultPaymentMethod$.next(selectedPaymentMethod);
    if (this.data.paymentPlanUi?.paymentPlan.paymentMethodId != undefined && this.selectedPaymentMethod$.value == undefined) {
      return;
    }

    this.selectedPaymentMethod$.next(selectedPaymentMethod);
    if (this.paymentMethodMode$.value !== PaymentMethodMode.SAVED) {
      this.paymentMethodMode$.next(PaymentMethodMode.SAVED);
    }

    this.paymentMethodIsFromUser = true;

    // PAYMENT_PLAN_PAYMENT_METHOD_SELECTED is dispatched by SelectPaymentMethodComponent
    // (workflow=PAYMENT_PLAN). Don't re-emit it here.

    this.changeDetectorRef.detectChanges();
  }

  onSelectPaymentMethodComponentClicked(event: MouseEvent): void {
    if (!this.didClickComeFromSavedPaymentMethod(event.target as HTMLElement)) {
      return;
    }

    this.dialog.open(RemoveOtherUserPaymentMethodComponent).afterClosed().pipe(
      take(1),
      tap((didConfirm: { data: boolean }) => {
        if (didConfirm?.data) {
          this.paymentMethodIsFromUser = true;
          if (this.defaultPaymentMethod$.value != undefined) {
            this.selectedPaymentMethod$.next(this.defaultPaymentMethod$.value);
          }
        } else {
          this.paymentMethodMode$.next(PaymentMethodMode.NONE);
        }
      }),
    ).subscribe();
  }

  didClickComeFromSavedPaymentMethod(element: HTMLElement): boolean {
    // Base case: if the current element is the body tag or nullish (in case of detached elements or document)
    if (element == undefined || element.tagName === 'BODY') {
      return false;
    }

    // Check if the current element's tag name includes the substring (case-insensitive)
    if (element.tagName.toLowerCase().includes('select-saved-payment-method')) {
      return true;
    }

    // Recursive step: move up to the parent element if the substring was not found
    return this.didClickComeFromSavedPaymentMethod(element.parentElement);
  }

  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);

  savedPaymentMethodsExist$ = this.paymentPlatformService.getSavedPaymentMethodsExist().pipe(
    tap(savedPaymentMethodsExist => {
      if (savedPaymentMethodsExist) {
        this.paymentMethodMode$.next(PaymentMethodMode.SAVED);
      }
    }),
  );

  setPaymentMethodIsFromUser$ = defer(() => {
    if (!this.data.paymentPlanUi?.paymentPlan.paymentMethodId) {
      return of(true);
    }

    return this.paymentPlatformService.getSavedPaymentMethod(
      this.data.paymentPlanUi?.paymentPlan.paymentMethodId,
    ).pipe(
      take(1),
      map(savedPaymentMethod => savedPaymentMethod != null),
      tap(paymentMethodExists => this.paymentMethodIsFromUser = paymentMethodExists),
      takeUntil(this._unsubscribe$),
    );
  });

  isProcessingRequest$ = new BehaviorSubject<boolean>(false);
  didRequestError$ = new BehaviorSubject<boolean>(false);

  controlHasError(controlName: PaymentPlanFormKey, error?: string): boolean {
    const control = this.paymentPlanForm.controls[controlName];
    if (control == undefined) {
      return false;
    }

    if (!error) {
      return control.errors != undefined && (control.touched || control.dirty);
    }

    return control.hasError(error) && (control.touched || control.dirty);
  }

  @ViewChildren(SelectPaymentMethodComponent) selectPaymentMethodComponent: QueryList<SelectPaymentMethodComponent & HTMLElement>;
  @ViewChildren(RecipientEmailsComponent) recipientEmailsComponent: QueryList<RecipientEmailsComponent & HTMLElement>;
  validate(): Observable<boolean> {
    this.paymentPlanForm.markAllAsTouched();
    if (this.paymentPlanForm.invalid) {
      return of(false);
    }

    return combineLatest([
      this.selectPaymentMethodComponent.first.validate(),
      this.recipientEmailsComponent.first.validate(),
    ]).pipe(
      mapToAndResult(),
    );
  }

  savePaymentPlan(): void {
    this.isProcessingRequest$.next(true);

    const isEdit = this.data.isEditPaymentPlan;
    const existingPlanId: string | undefined = this.data.paymentPlanUi?.paymentPlan?.id != undefined
      ? String(this.data.paymentPlanUi.paymentPlan.id)
      : undefined;
    const operationKey = 'PAYMENT_PLAN_SAVE';

    // validate
    this.validate().pipe(
      take(1),
      ignoreFalsy(),
      tap(_ => this.didRequestError$.next(false)),
      switchMap(_ => {
        const paymentPlanCreatedByIds: PaymentPlanCreatedByIds = this.getCreatedByIds();
        const paymentPlanFormData = this.paymentPlanForm.value as PaymentPlanFormData;

        const newPaymentPlan = this.getPaymentPlanPayload(paymentPlanFormData, paymentPlanCreatedByIds);

        this.paymentPlanTrackService.trackWorkflow(
          PaymentPlanWorkflowEvent.PAYMENT_PLAN_SUBMITTED,
          { cadence: newPaymentPlan.frequency, totalAmount: Number(newPaymentPlan.amount) },
        );
        this.paymentPlanTrackService.startOperation(operationKey);

        return this.paymentPlanApiService.persistPaymentPlan(
          newPaymentPlan,
          paymentPlanCreatedByIds.stripeUserId,
          this.selectedPaymentMethod$.value.billingDetails.address?.state,
          this.authAdaptor.isAdmin,
        ).pipe(
          map(response => ({ response, payload: newPaymentPlan })),
        );
      }),
      take(1),
      tap({
        next: ({ response, payload }) => {
          this._didEmitTerminalEvent = true;
          const operationDurationMs = this.paymentPlanTrackService.endOperation(operationKey);
          if (isEdit) {
            this.paymentPlanTrackService.trackWorkflow(
              PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_SUCCEEDED,
              { planId: existingPlanId, operationDurationMs },
            );
          } else {
            const createdId = (response as { data?: { CreateRecurringPaymentPlan?: { id?: unknown } } })
              ?.data?.CreateRecurringPaymentPlan?.id;
            // createdId should always be defined when persistPaymentPlan succeeds; the empty-string fallback
            // exists only as a defensive measure if the API ever returns success without an id.
            if (createdId == undefined) {
              this.logger.warn('PAYMENT_PLAN_CREATE_SUCCEEDED fired with empty planId — API succeeded without returning an id');
            }
            this.paymentPlanTrackService.trackWorkflow(
              PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_SUCCEEDED,
              {
                planId: createdId != undefined ? String(createdId) : '',
                cadence: payload.frequency,
                totalAmount: Number(payload.amount),
                operationDurationMs,
              },
            );
          }

          this.paymentPlanService.paymentPlansUpdated$.next();
          this.store.dispatch(new ClearScheduledPayments());
          this.dialogRef.close();
        },
        error: (error: unknown) => {
          const operationDurationMs = this.paymentPlanTrackService.endOperation(operationKey);
          const failureContext = extractFailureContext(error);
          if (isEdit) {
            this.paymentPlanTrackService.trackWorkflow(
              PaymentPlanWorkflowEvent.PAYMENT_PLAN_UPDATE_FAILED,
              { ...failureContext, planId: existingPlanId, operationDurationMs },
            );
          } else {
            this.paymentPlanTrackService.trackWorkflow(
              PaymentPlanWorkflowEvent.PAYMENT_PLAN_CREATE_FAILED,
              { ...failureContext, operationDurationMs },
            );
          }
        },
      }),
      takeUntil(this._unsubscribe$),
      finalize(() => this.isProcessingRequest$.next(false)),
    ).subscribe();
  }


  /**
   * If payment method is from user get if current user is staff or contact.
   * Else return the original users (the one who created the recurring payment) info.
   */
  getCreatedByIds(): PaymentPlanCreatedByIds {
    if (this.paymentMethodIsFromUser) {
      return {
        staffId: this.authAdaptor.getStaffIdOrNull,
        contactId: this.authAdaptor.getContactIdOrNull,
        stripeUserId: this.store.selectSnapshot(StripeState.getUserId),
      };
    } else {
      return {
        staffId: this.data.paymentPlanUi?.staff?.id,
        contactId: this.data.paymentPlanUi?.contact?.id,
        stripeUserId: this.getStripeUserIdForOtherUsersPaymentMethod(this.data.paymentPlanUi?.contact != null),
      };
    }
  }

  /**
   * Returns one of the following:
   */
  getStripeUserIdForOtherUsersPaymentMethod(isContact: boolean): string {
    if (isContact) {
      return this.data.paymentPlanUi.contact.stripeUserId;
    }

    return this.data.paymentPlanUi.client.firmStripeCustomerId;
  }

  /**
   * Destroy subscriptions.
   */
  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}

export interface PaymentPlanCreatedByIds {
  staffId: number,
  contactId: number,
  stripeUserId: string
}
